import { useEffect } from "react";

import type { PositionedWord } from "@edclub/crosswords-engine";
import { cn } from "@edclub/utils";

import { GLOSSARY_IMAGE_URL } from "@/const/glossary";
import { useCrosswordStore } from "@/store";

export function Clues() {
  const crossword = useCrosswordStore.use.crossword();
  const activeWordId = useCrosswordStore.use.activeWordId();
  const setActiveWord = useCrosswordStore.use.setActiveWord();
  const setActiveCell = useCrosswordStore.use.setActiveCell();
  const useImagesIfAvailable = useCrosswordStore.use.useImagesIfAvailable();
  const imageSize = useCrosswordStore.use.imageSize();
  const isChecked = useCrosswordStore.use.isChecked();

  useEffect(() => {
    if (activeWordId !== null) {
      const clue = document.getElementById(`clue-for-${activeWordId}`);

      if (clue) {
        clue.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [activeWordId]);

  if (!crossword) {
    return <div>{t("Loading clues...")}</div>;
  }

  const clues = [
    {
      label: t("Across"),
      records: Object.entries(crossword.across),
    },
    {
      label: t("Down"),
      records: Object.entries(crossword.down),
    },
  ];

  const handleClueClick = (clue: PositionedWord) => () => {
    if (isChecked) {
      return;
    }

    const wordStartingRow = clue.row;
    const wordStartingCol = clue.col;
    const isAcross = clue.direction === "across";
    const wordLastCol = wordStartingCol + clue.answer.length - 1;
    const wordLastRow = wordStartingRow + clue.answer.length - 1;

    if (isAcross) {
      let colIndex = wordStartingCol;
      let currentCell;
      let success = false;
      const firstCell = crossword.grid[wordStartingRow][colIndex];

      while (colIndex <= wordLastCol) {
        currentCell = crossword.grid[wordStartingRow][colIndex];

        if (
          currentCell.letter &&
          !currentCell.userAnswer &&
          !currentCell.revealed
        ) {
          success = true;
          setActiveWord(clue.id);
          setActiveCell(currentCell);
          break;
        }

        colIndex++;
      }

      if (!success) {
        setActiveWord(clue.id);
        setActiveCell(firstCell);
      }
    } else {
      let rowIndex = wordStartingRow;
      let currentCell;
      let success = false;
      const firstCell = crossword.grid[rowIndex][wordStartingCol];

      while (rowIndex <= wordLastRow) {
        currentCell = crossword.grid[rowIndex][wordStartingCol];

        if (
          currentCell.letter &&
          !currentCell.userAnswer &&
          !currentCell.revealed
        ) {
          success = true;
          setActiveWord(clue.id);
          setActiveCell(currentCell);
          break;
        }

        rowIndex++;
      }

      if (!success) {
        setActiveWord(clue.id);
        setActiveCell(firstCell);
      }
    }
  };

  const handleKeyDown = (
    clue: PositionedWord,
    event: React.KeyboardEvent<HTMLLIElement>,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClueClick(clue)();
    }
  };

  return (
    <div className="flex flex-col gap-6" aria-label={t("Puzzle clues")}>
      {clues.map((clueSection) => (
        <section
          key={clueSection.label}
          aria-labelledby={`section-${clueSection.label}`}
        >
          <h2
            id={`section-${clueSection.label}`}
            className="mb-2 text-lg font-bold text-[#42576e]"
          >
            {clueSection.label}
          </h2>
          <ul>
            {clueSection.records.map(([num, clue]) => (
              <li
                id={`clue-for-${clue.id}`}
                key={`clue-${num}-${clue.id}`}
                className="text-md mb-0.5 cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={handleClueClick(clue)}
                onKeyDown={(event) => handleKeyDown(clue, event)}
                aria-pressed={clue.id === activeWordId}
              >
                <span className="mr-2 font-semibold select-none">{num}.</span>
                <span
                  className={cn(
                    "cursor-pointer select-none hover:text-[#329DA7]",
                    clue.id === activeWordId && "font-semibold text-[#329DA7]",
                  )}
                >
                  {useImagesIfAvailable && clue.image ? (
                    <img
                      className={cn(
                        "my-1 inline-block",
                        imageSize === "compact" && "size-40",
                        imageSize === "full" && "size-full",
                      )}
                      src={GLOSSARY_IMAGE_URL + clue.image}
                      alt={clue.clue}
                    />
                  ) : (
                    clue.clue
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
